import { apiCall } from './apiCall';
import config from '../config/config';

const BASE_URL = `${config.POSTLOGIN_API_URL_COMPANY}documents`;
const AUTH_UPLOAD_URL = `${config.AUTH_API_URL_COMPANY}uploadFile`;

// ---- Documents ----

export const listDocuments = async ({ documentType, questionType, questionId, financialYearId } = {}) => {
  const query = new URLSearchParams();
  if (documentType) query.append('documentType', JSON.stringify(documentType));
  if (questionType) query.append('questionType', questionType);
  if (questionId != null) query.append('questionId', questionId);
  if (financialYearId != null) query.append('financialYearId', financialYearId);
  const qs = query.toString();
  const response = await apiCall(`${BASE_URL}${qs ? `?${qs}` : ''}`, {}, {}, 'GET');
  return response?.data?.documents || [];
};

export const getDocument = async (id) => {
  const response = await apiCall(`${BASE_URL}/${id}`, {}, {}, 'GET');
  return response?.data;
};

/**
 * Uploads a raw file and returns its FileMetadataDTO reference.
 * Use the returned { fileMetadataId, fileMetadata } to build a
 * SaveDocumentDto (documents module) or an UploadedFileMetadataDto
 * (quantitative/qualitative answer saves).
 */
export const uploadFileMetadata = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiCall(AUTH_UPLOAD_URL, {}, formData, 'POST', false, false);
  const data = response?.data?.data;
  return {
    fileMetadataId: data?.uuid ?? data?.id,
    fileMetadata: data,
    isExisting: response?.data?.isExisting,
  };
};

export const createDocument = async (payload) => {
  const response = await apiCall(BASE_URL, {}, payload, 'POST');
  return response?.data;
};

export const updateDocument = async (id, payload) => {
  const response = await apiCall(`${BASE_URL}/${id}`, {}, payload, 'POST');
  return response?.data;
};

export const deleteDocument = async (id) => {
  const response = await apiCall(`${BASE_URL}/${id}`, {}, {}, 'DELETE');
  return response?.data;
};

// ---- Documents Batch ----

export const createBatch = async (payload) => {
  const response = await apiCall(`${BASE_URL}/batches`, {}, payload, 'POST');
  return response?.data;
};

export const listBatches = async () => {
  const response = await apiCall(`${BASE_URL}/batches`, {}, {}, 'GET');
  return response?.data || [];
};

export const getBatch = async (id) => {
  const response = await apiCall(`${BASE_URL}/batches/${id}`, {}, {}, 'GET');
  return response?.data;
};

export const uploadBatchFiles = async (batchId, files = []) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const response = await apiCall(`${BASE_URL}/batches/${batchId}/files`, {}, formData, 'POST');
  return response?.data || [];
};

/**
 * Consolidated quantitative-evidence upload — replaces the two-step
 * createBatch + uploadBatchFiles flow for new uploads. Files must already
 * be uploaded (via uploadFileMetadata) before calling this.
 */
export const uploadQuantitativeEvidence = async (payload) => {
  const response = await apiCall(`${BASE_URL}/upload`, {}, payload, 'POST');
  return response?.data;
};

export const getBatchExtractedKpisSummary = async (batchId) => {
  const response = await apiCall(`${BASE_URL}/batches/${batchId}/extracted-kpis`, {}, {}, 'GET');
  return response?.data || [];
};

export const saveBatchKpis = async (batchId, kpis, deletedKpis = []) => {
  const response = await apiCall(`${BASE_URL}/batches/${batchId}/kpis`, {}, { kpis, deletedKpis }, 'POST');
  return response?.data;
};

/** Bulk-reschedules failed/timed-out DocSense analysis — one call for the whole
 *  selection, never a loop of single-document calls. The backend aggregates and
 *  returns a summary message (shown via the global alert), plus the raw
 *  { succeeded, skipped } ids so the caller can clear/refresh accordingly. */
export const retryAnalysis = async (documentIds) => {
  const response = await apiCall(`${BASE_URL}/retry-analysis`, {}, { documentIds }, 'POST');
  return response?.data;
};

/** Manually marks one or more documents as verified — a human vouching for the
 *  evidence directly, independent of the shouldParse/reviewed-KPI paths. */
export const markVerified = async (documentIds) => {
  const response = await apiCall(`${BASE_URL}/mark-verified`, {}, { documentIds }, 'POST');
  return response?.data;
};
